import { useConfirmationDialog } from '@factorialco/deprecated-design-system'
import {
  PerformanceProcessAgreementHeaderActionsFragment,
  PerformanceProcessAgreementSignAsEmployeeDocument,
  PerformanceProcessAgreementSignAsManagerDocument,
  PerformanceProcessAgreementUnsignAsEmployeeDocument,
  PerformanceProcessAgreementUnsignAsManagerDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import i18n from 'modules/core/lib/i18n'

interface PrimaryAction {
  label: string
  onClick: () => void
  disabled?: boolean
}

interface SecondaryAction extends PrimaryAction {
  variant?: 'outline' | 'critical'
}

interface UseActionsReturnType {
  primaryAction: PrimaryAction | undefined
  secondaryActions: SecondaryAction[]
}

export const useActions = (
  agreement: PerformanceProcessAgreementHeaderActionsFragment
): UseActionsReturnType => {
  const { openDialog } = useConfirmationDialog()
  const { access } = useCurrentSession()

  const [signAsManager] = useMutation(PerformanceProcessAgreementSignAsManagerDocument, {
    extractorFn: (data) => data?.performanceMutations?.signAsManagerPerformanceAgreement,
  })

  const [signAsEmployee] = useMutation(PerformanceProcessAgreementSignAsEmployeeDocument, {
    extractorFn: (data) => data?.performanceMutations?.signAsEmployeePerformanceAgreement,
  })

  const [unsignAsManager] = useMutation(PerformanceProcessAgreementUnsignAsManagerDocument, {
    extractorFn: (data) => data?.performanceMutations?.unsignAsManagerPerformanceAgreement,
  })

  const [unsignAsEmployee] = useMutation(PerformanceProcessAgreementUnsignAsEmployeeDocument, {
    extractorFn: (data) => data?.performanceMutations?.unsignAsEmployeePerformanceAgreement,
  })

  let primaryAction: PrimaryAction | undefined = undefined
  const secondaryActions: SecondaryAction[] = []

  const targetName = agreement.target?.access?.firstName
  const reviewerName = agreement.reviewer?.firstName
  const signerName = agreement.signer?.firstName

  const esignatureEnabled = agreement.process?.agreementsConfiguration?.esignatureEnabled
  const isLastSigner = Boolean(agreement.managerSignedAt || agreement.targetSignedAt)

  const unsignAsTargetAction: SecondaryAction = {
    label: i18n.t('performance_process.agreements.actions.edit.button'),
    variant: 'outline',
    onClick: () =>
      openDialog({
        title: i18n.t('performance_process.agreements.actions.edit.dialog.title'),
        description: i18n.t('performance_process.agreements.actions.edit.dialog.description', {
          name: signerName || reviewerName,
        }),
        illustration: 'delete-cross',
        confirmLabel: i18n.t('common.edit'),
        onConfirm: () =>
          unsignAsEmployee({
            variables: {
              agreementId: agreement.id,
            },
          }),
      }),
  }

  const unsignAsTargetForManagerAction: SecondaryAction = {
    label: i18n.t('performance_process.agreements.actions.edit.button'),
    variant: 'outline',
    onClick: () =>
      openDialog({
        title: i18n.t('performance_process.agreements.actions.edit.dialog.title'),
        description: i18n.t('performance_process.agreements.actions.edit.dialog.description', {
          name: targetName,
        }),
        illustration: 'delete-cross',
        confirmLabel: i18n.t('common.edit'),
        onConfirm: () =>
          unsignAsEmployee({
            variables: {
              agreementId: agreement.id,
            },
          }),
      }),
  }

  const unsignAsManagerAction: SecondaryAction = {
    label: i18n.t('performance_process.agreements.actions.edit.button'),
    variant: 'outline',
    onClick: () =>
      openDialog({
        title: i18n.t('performance_process.agreements.actions.edit.dialog.title'),
        description: i18n.t('performance_process.agreements.actions.edit.dialog.description', {
          name: targetName,
        }),
        illustration: 'delete-cross',
        confirmLabel: i18n.t('common.edit'),
        onConfirm: () =>
          unsignAsManager({
            variables: {
              agreementId: agreement.id,
            },
          }),
      }),
  }

  const signAsTargetAction: PrimaryAction = {
    label: i18n.t('performance_process.agreements.actions.submit.button'),
    onClick: () => {
      const lastSignerDescription = esignatureEnabled
        ? i18n.t(
            'performance_process.agreements.actions.submit.dialog.description_last_submission_esignature'
          )
        : i18n.t('performance_process.agreements.actions.submit.dialog.description_last_submission')

      const description = isLastSigner
        ? lastSignerDescription
        : i18n.t('performance_process.agreements.actions.submit.dialog.description')

      openDialog({
        title: i18n.t('performance_process.agreements.actions.submit.dialog.title'),
        description: description,
        illustration: 'check',
        confirmLabel: i18n.t('common.submit'),
        onConfirm: () =>
          signAsEmployee({
            variables: {
              agreementId: agreement.id,
            },
          }),
      })
    },
  }

  const signAsManagerAction: PrimaryAction = {
    label: i18n.t('performance_process.agreements.actions.submit.button'),
    onClick: () => {
      const lastSignerDescription = esignatureEnabled
        ? i18n.t(
            'performance_process.agreements.actions.submit.dialog.description_last_submission_esignature'
          )
        : i18n.t('performance_process.agreements.actions.submit.dialog.description_last_submission')

      const description = isLastSigner
        ? lastSignerDescription
        : i18n.t('performance_process.agreements.actions.submit.dialog.description')

      openDialog({
        title: i18n.t('performance_process.agreements.actions.submit.dialog.title'),
        description: description,
        illustration: 'check',
        confirmLabel: i18n.t('common.submit'),
        onConfirm: () =>
          signAsManager({
            variables: {
              agreementId: agreement.id,
            },
          }),
      })
    },
  }

  if (agreement._policies?.unsignAsTarget.can) {
    const isTarget = agreement.target?.access?.id === access.id
    secondaryActions.push(isTarget ? unsignAsTargetAction : unsignAsTargetForManagerAction)
  }

  if (agreement._policies?.unsignAsManager.can) {
    secondaryActions.push(unsignAsManagerAction)
  }

  if (agreement._policies?.signAsTarget.can) {
    primaryAction = signAsTargetAction
  }

  if (agreement?._policies?.signAsManager.can) {
    primaryAction = signAsManagerAction
  }

  return { primaryAction, secondaryActions }
}
